import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import { TypeOption } from '../../Certifications/types';
import { fetchCertificationTypes } from 'modules/state-mgmt/cache/actions';

/**
 * Hook to get certification type options for MyCertifications page
 * This is a simplified version that doesn't require projectId or certification groups
 */
export const useCertificationTypeOptions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { certificationTypes, languageCode } = useAppSelector(state => ({
    certificationTypes: state.cache.certificationTypes,
    languageCode: state.app?.languageCode || 'en',
  }));

  // Load certification types if not already loaded
  useEffect(() => {
    const loadCertificationTypes = async () => {
      if (!certificationTypes || (!certificationTypes.en?.length && !certificationTypes.es?.length)) {
        setIsLoading(true);
        try {
          await dispatch(fetchCertificationTypes({ languageCode }));
        } catch (error) {
          console.error('Failed to load certification types:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadCertificationTypes();
  }, [dispatch, languageCode, certificationTypes]);

  // Generate type options from certification types
  const typeOptions = useMemo((): TypeOption[] => {
    if (!certificationTypes || (!certificationTypes.en?.length && !certificationTypes.es?.length)) {
      return [];
    }

    const allCertTypes = certificationTypes[languageCode] || [];

    if (!allCertTypes.length) {
      return [];
    }

    // Create options without groups (all in "Other" group)
    const options: TypeOption[] = allCertTypes.map(certType => ({
      value: certType,
      label: certType.name,
      group: t('certifications.addModal.otherGroup', 'Other'),
    }));

    return options;
  }, [certificationTypes, languageCode, t]);

  return {
    typeOptions,
    certificationTypes: certificationTypes || { en: [], es: [] },
    isLoading,
  };
};
